// Diagnóstico de la obra: hallazgos con acción concreta + día típico + semana tipo + resumen para WhatsApp.
import React, { useMemo, useState } from 'react';
import { ComposedChart, Area, Line, Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { diagnose, profiles, whatsappSummary } from './insights';
import { periodKpis, fmtMin } from './iaData';
import { T, Btn } from './ui';

const LV = {
  alerta: { c: '#dc2626', bg: '#fef2f2', label: 'Atención' },
  oportunidad: { c: '#d97706', bg: '#fffbeb', label: 'Oportunidad' },
  info: { c: '#2563eb', bg: '#eff6ff', label: 'Dato' },
  bien: { c: '#059669', bg: '#ecfdf5', label: 'Bien' },
};

export default function DiagnosisPanel({ photos, cfg, fromKey, toKey, name, rangeLabel, onReview, internal }) {
  const items = useMemo(() => diagnose(photos, cfg, fromKey, toKey), [photos, cfg, fromKey, toKey]);
  const prof = useMemo(() => profiles(photos, cfg), [photos, cfg]);
  const [copied, setCopied] = useState(false);
  const [all, setAll] = useState(false);

  const copy = async () => {
    const txt = whatsappSummary(name, rangeLabel, periodKpis(photos, cfg), items);
    try { await navigator.clipboard.writeText(txt); } catch (e) {
      const ta = document.createElement('textarea'); ta.value = txt; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };

  if (!photos.length) return <div style={{ color: T.muted, padding: 16 }}>No hay fotos en el período para analizar.</div>;
  const counts = items.reduce((a, i) => ({ ...a, [i.level]: (a[i.level] || 0) + 1 }), {});
  const shown = all ? items : items.slice(0, 6);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        {['alerta', 'oportunidad', 'bien'].filter((l) => counts[l]).map((l) => (
          <span key={l} style={{ background: LV[l].bg, color: LV[l].c, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
            {counts[l]} {l === 'alerta' ? (counts[l] === 1 ? 'para atender' : 'para atender') : l === 'oportunidad' ? (counts[l] === 1 ? 'oportunidad' : 'oportunidades') : 'en orden'}
          </span>
        ))}
        <div style={{ flex: 1 }} />
        <Btn onClick={copy} title="Copia un resumen listo para pegar en WhatsApp">{copied ? '✓ Copiado' : '📋 Copiar resumen para WhatsApp'}</Btn>
        <Btn onClick={() => window.print()} title="Imprimir o guardar como PDF">🖨 Imprimir</Btn>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 12 }}>
        {shown.map((i) => (
          <div key={i.key} style={{ border: `1px solid ${T.line}`, borderLeft: `4px solid ${LV[i.level].c}`, borderRadius: 14, padding: 14, background: '#fff', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: LV[i.level].c, background: LV[i.level].bg, borderRadius: 999, padding: '1px 8px' }}>{LV[i.level].label}</span>
            </div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{i.titulo}</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: LV[i.level].c }}>{i.dato}</div>
            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.5 }}>{i.detalle}</div>
            <div style={{ fontSize: 13, lineHeight: 1.5, background: T.bg, borderRadius: 10, padding: '8px 10px' }}>
              <b>Qué hacer:</b> {i.accion}
            </div>
            {internal && i.cta === 'revisar' && <Btn active onClick={onReview} style={{ alignSelf: 'flex-start' }}>⛑ Revisar cascos</Btn>}
          </div>
        ))}
      </div>
      {items.length > 6 && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <Btn onClick={() => setAll(!all)}>{all ? 'Ver menos' : `Ver los ${items.length} hallazgos`}</Btn>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 18 }}>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Un día típico en la obra</div>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>Personas promedio cada media hora y % con casco. La franja gris es la jornada pactada.</div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={prof.day} margin={{ top: 6, right: 4, left: -18, bottom: 0 }} accessibilityLayer={false}>
                <CartesianGrid stroke={T.line} vertical={false} />
                <XAxis dataKey="min" type="number" domain={['dataMin', 'dataMax']} tickFormatter={fmtMin} tick={{ fill: T.muted, fontSize: 11 }} stroke={T.line} />
                <YAxis yAxisId="l" tick={{ fill: T.muted, fontSize: 11 }} stroke={T.line} />
                <YAxis yAxisId="r" orientation="right" domain={[0, 100]} hide />
                <ReferenceArea yAxisId="l" x1={cfg.jIni} x2={cfg.jFin} fill="#94a3b8" fillOpacity={0.08} />
                <Tooltip labelFormatter={fmtMin} formatter={(v, n) => (n === 'casco' ? [v + '%', '% con casco'] : [v, n === 'personas' ? 'Personas' : 'Actividad'])} />
                <Area yAxisId="l" dataKey="personas" type="monotone" stroke={T.green} fill={T.green} fillOpacity={0.18} strokeWidth={2} isAnimationActive={false} />
                <Line yAxisId="r" dataKey="casco" type="monotone" stroke={T.yellow} dot={false} strokeWidth={2} connectNulls isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Semana tipo</div>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>Horas-hombre promedio según el día de la semana.</div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={prof.week} margin={{ top: 6, right: 4, left: -18, bottom: 0 }} accessibilityLayer={false}>
                <CartesianGrid stroke={T.line} vertical={false} />
                <XAxis dataKey="dia" tick={{ fill: T.muted, fontSize: 12 }} stroke={T.line} />
                <YAxis tick={{ fill: T.muted, fontSize: 11 }} stroke={T.line} />
                <Tooltip formatter={(v, n, p) => [`${v} h-h (${p.payload.dias} ${p.payload.dias === 1 ? 'día' : 'días'})`, 'Promedio']} />
                <Bar dataKey="hh" fill={T.blue} radius={[6, 6, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
